import { useEffect, useMemo, useState } from "react";

const EMPTY_ARRAY = [];
const EMPTY_SORTERS = {};

const readValue = (item, path) => {
  return path.split(".").reduce((value, key) => value?.[key], item);
};

const normalizeSearchValue = (value) => value?.toString().trim().toLowerCase();

const collectPrimitiveValues = (value) => {
  if (value == null) return [];
  if (["string", "number", "boolean"].includes(typeof value)) return [value];
  if (Array.isArray(value)) return value.flatMap(collectPrimitiveValues);
  if (typeof value === "object") {
    return Object.values(value).flatMap(collectPrimitiveValues);
  }
  return [];
};

const matchesSearch = (item, fields, search) => {
  const query = normalizeSearchValue(search);
  if (!query) return true;

  const fieldValues = fields
    .map((field) => readValue(item, field))
    .filter((value) => value != null && value !== "");
  const searchableValues =
    fieldValues.length > 0 ? fieldValues : collectPrimitiveValues(item);

  return searchableValues.some((value) =>
    normalizeSearchValue(value)?.includes(query),
  );
};

export const useUserTable = ({
  data = EMPTY_ARRAY,
  searchFields = EMPTY_ARRAY,
  sorters = EMPTY_SORTERS,
  filters = EMPTY_ARRAY,
  pageSize = 10,
}) => {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [page, setPage] = useState(1);

 const processedData = useMemo(() => {
  const source = Array.isArray(data) ? data : [];

  let rows = source.filter((item) =>
    matchesSearch(item, searchFields, search)
  );

  filters.forEach((filter) => {
    if (typeof filter === "function") {
      rows = rows.filter(filter);
    }
  });

  if (sortBy && sorters[sortBy]) {
    rows = [...rows].sort(sorters[sortBy]);
  }

  return rows;
}, [data, filters, search, searchFields, sortBy, sorters]);

  useEffect(() => {
    setPage(1);
  }, [search, sortBy, filters]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [page, pageSize, processedData]);

  return {
    search,
    setSearch,
    sortBy,
    setSortBy,
    page,
    setPage,
    processedData,
    paginatedData,
    total: processedData.length,
    pageSize,
  };
};

export const userSorters = {
  latest: (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
  oldest: (a, b) => new Date(a.createdOn) - new Date(b.createdOn),
  amountHigh: (a, b) => (b.amount || 0) - (a.amount || 0),
  amountLow: (a, b) => (a.amount || 0) - (b.amount || 0),
  name: (a, b) => (a.name || a.holderName || "").localeCompare(b.name || b.holderName || ""),
};

export const userSortOptions = [
  { label: "Latest First", value: "latest" },
  { label: "Oldest First", value: "oldest" },
  { label: "Amount High to Low", value: "amountHigh" },
  { label: "Amount Low to High", value: "amountLow" },
  { label: "Reset", value: "" },
];
